import './DesignCSPage.scss';

import React from 'react';
import { generatePath, NavLink, RouteComponentProps } from 'react-router-dom';
import { PATH } from '../../../router';
import { recaptchaKey, recaptchaFormKey } from '../../../common/constants';
import { mutationMainForm } from '../../../network';
import { Grid, GridItem } from '@dataartdev/uikit/Grid';
import { Picture } from '@dataartdev/uikit/Picture';
import { Typography } from '@dataartdev/uikit/Typography';
import { Breadcrumbs } from '@dataartdev/uikit/Breadcrumbs';
import { CaseStudyAdvantages } from '../../../common/components/CaseStudyAdvantages/CaseStudyAdvantages';
import { NoteText } from '../../../common/components/NoteText/NoteText';
import { TagGroup } from '@dataartdev/uikit/TagGroup';
import { ContentBuilder } from '../../../common/components/ContentBuilder/ContentBuilder';
import { SiteBlock } from '../../../common/components/SiteBlock/SiteBlock';
import { cn } from '../../../ui/utils/bem';
import { useIntl } from 'react-intl';
import { ICasePage } from './models/CasePage.props';
import { useLetsTalkFormDictionary } from '../../../common/hooks/useLetsTalkFormDictionary';
import { SocialsShare } from '@dataartdev/uikit/SocialsShare';
import { onSocialShareHandler } from '../../../common/utils/socialShareHandler';

export const cnDesignCSPage = cn('DesignCSPage');

type Props = RouteComponentProps & {
  data: ICasePage | null;
};

export const DesignCSPage: React.FC<Props> = ({ history, data }) => {
  const { locale } = useIntl();
  const lang = locale === 'en' ? undefined : locale;

  const {
    breadcrumbs = [],
    clientTitle,
    locationText,
    title,
    achievements,
    clientDescription,
    challenge,
    industry,
    services = [],
    clientLogo,
    solution,
    relatedCaseStudies = [],
    techStock = [],
    picture,
    content,
    clientSlug,
    tags = [],
  } = data || {};

  const {
    companySiteCaseStudy,
    dictionariesForm,
  } = useLetsTalkFormDictionary();

  const {
    companySiteCaseStudyCompanyName = 'COMPANY NAME',
    companySiteCaseStudyLocation = 'LOCATION',
    companySiteCaseStudyIndustry = 'INDUSTRY',
    companySiteCaseStudyServices = 'SERVICES',
    companySiteCaseStudyTechnologies = 'TECHNOLOGIES',
    companySiteCaseStudyMoreCases = 'TAGS',
    companySiteCaseStudyShare = '',
  } = companySiteCaseStudy || {};

  const mainContent = content?.filter(item => item.type !== 3);
  const formContent = content?.filter(item => item.type === 3);

  return (
    <div className={cnDesignCSPage()}>
      <div>
        <div className={cnDesignCSPage('Breadcrumbs')}>
          {breadcrumbs?.length > 0 && (
            <div className="container">
              <Breadcrumbs
                getLabel={page => page.text}
                getLink={page => page.url}
                history={history}
                pages={breadcrumbs}
              />
            </div>
          )}

          <div className="container">
            <Grid
              breakpoints={{
                desktop: { cols: 12 },
                tablet: { cols: 8 },
                mobile: { cols: 2 },
              }}
              className={cnDesignCSPage('Top')}
            >
              <GridItem
                breakpoints={{
                  desktop: { col: 10 },
                  tablet: { col: 8 },
                  mobile: { col: 2 },
                }}
              >
                {title && (
                  <Typography.Title size="lg" as="h1" parse>
                    {title}
                  </Typography.Title>
                )}
              </GridItem>
            </Grid>
          </div>

          {picture && (
            <div className={cnDesignCSPage('Preview')}>
              <Picture {...picture} />
            </div>
          )}
          <div className="container">
            <SiteBlock useMargin topGap="xxl" bottomGap="xxl">
              <Grid
                breakpoints={{
                  desktop: { cols: 12, gap: 'xl' },
                  tablet: { cols: 8 },
                }}
              >
                {(achievements ||
                  clientDescription ||
                  challenge ||
                  solution) && (
                  <GridItem
                    breakpoints={{ desktop: { col: 8 }, tablet: { col: 8 } }}
                  >
                    {achievements && (
                      <CaseStudyAdvantages achievements={achievements} />
                    )}
                    {clientDescription && (
                      <NoteText
                        title={clientDescription.title}
                        content={clientDescription.text}
                        strings={clientDescription.strings}
                      />
                    )}
                    {challenge && (
                      <NoteText
                        title={challenge.title}
                        content={challenge.text}
                        strings={challenge.strings}
                      />
                    )}
                    {solution && (
                      <NoteText
                        title={solution.title}
                        content={solution.text}
                        strings={solution.strings}
                      />
                    )}
                  </GridItem>
                )}

                <GridItem
                  breakpoints={{
                    desktop: { col: 4, colStart: 9 },
                    tablet: { col: 8 },
                  }}
                >
                  <div className={cnDesignCSPage('RightBox')}>
                    {(clientTitle ||
                      locationText ||
                      industry ||
                      services?.length > 0 ||
                      techStock?.length > 0) && (
                      <div className={cnDesignCSPage('InfoBlock')}>
                        {clientTitle && (
                          <div className={cnDesignCSPage('InfoBlock-Item')}>
                            <div className={cnDesignCSPage('InfoBlock-Left')}>
                              {companySiteCaseStudyCompanyName && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                >
                                  {companySiteCaseStudyCompanyName}
                                </Typography.Text>
                              )}
                            </div>
                            {clientTitle && clientSlug && (
                              <div
                                className={cnDesignCSPage('InfoBlock-right')}
                              >
                                <NavLink
                                  to={generatePath(PATH.CLIENT_PAGE, {
                                    slug: clientSlug,
                                    lang,
                                  })}
                                >
                                  <Typography.Text weight="bold" size="m">
                                    {clientTitle}
                                  </Typography.Text>
                                </NavLink>
                              </div>
                            )}
                          </div>
                        )}
                        {locationText && (
                          <div className={cnDesignCSPage('InfoBlock-Item')}>
                            <div className={cnDesignCSPage('InfoBlock-Left')}>
                              {companySiteCaseStudyLocation && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                >
                                  {companySiteCaseStudyLocation}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnDesignCSPage('InfoBlock-right')}>
                              <Typography.Text weight="bold" size="m">
                                {locationText}
                              </Typography.Text>
                            </div>
                          </div>
                        )}
                        {industry && (
                          <div className={cnDesignCSPage('InfoBlock-Item')}>
                            <div className={cnDesignCSPage('InfoBlock-Left')}>
                              {companySiteCaseStudyIndustry && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                >
                                  {companySiteCaseStudyIndustry}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnDesignCSPage('InfoBlock-right')}>
                              {industry.slug && industry.title && (
                                <NavLink
                                  to={generatePath(PATH.INDUSTRIES_PAGE, {
                                    slug: industry.slug,
                                  })}
                                >
                                  <Typography.Text weight="bold" size="m">
                                    {industry.title}
                                  </Typography.Text>
                                </NavLink>
                              )}
                            </div>
                          </div>
                        )}
                        {services?.length > 0 && (
                          <div className={cnDesignCSPage('InfoBlock-Item')}>
                            <div className={cnDesignCSPage('InfoBlock-Left')}>
                              {companySiteCaseStudyServices && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                >
                                  {companySiteCaseStudyServices}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnDesignCSPage('InfoBlock-right')}>
                              {services.map(
                                page =>
                                  page.slug &&
                                  page.title && (
                                    <NavLink
                                      key={page.slug}
                                      to={generatePath(PATH.SERVICES_PAGE, {
                                        slug: page.slug,
                                      })}
                                    >
                                      <Typography.Text weight="bold" size="m">
                                        {page.title}
                                      </Typography.Text>
                                    </NavLink>
                                  )
                              )}
                            </div>
                          </div>
                        )}
                        {techStock?.length > 0 && (
                          <div className={cnDesignCSPage('InfoBlock-Item')}>
                            <div className={cnDesignCSPage('InfoBlock-Left')}>
                              {companySiteCaseStudyTechnologies && (
                                <Typography.Text
                                  weight="bold"
                                  transform="uppercase"
                                  size="xs"
                                  nowrap
                                >
                                  {companySiteCaseStudyTechnologies}
                                </Typography.Text>
                              )}
                            </div>
                            <div className={cnDesignCSPage('InfoBlock-right')}>
                              <TagGroup
                                gap="xs"
                                items={techStock.map(tag => {
                                  return {
                                    ...tag,
                                    onClick: () =>
                                      history.push(
                                        generatePath(
                                          `${PATH.SEARCH_PAGE}?tags=${tag.id}`,
                                          {
                                            lang,
                                          }
                                        )
                                      ),
                                  };
                                })}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {clientLogo &&
                      clientSlug &&
                      Object.keys(clientLogo).length > 0 && (
                        <NavLink
                          to={generatePath(PATH.CLIENT_PAGE, {
                            slug: clientSlug,
                            lang,
                          })}
                        >
                          <div className={cnDesignCSPage('Logo')}>
                            <Picture {...clientLogo} />
                          </div>
                        </NavLink>
                      )}

                    {relatedCaseStudies?.length > 0 && (
                      <div className={cnDesignCSPage('Recommendation')}>
                        {companySiteCaseStudy && (
                          <div
                            className={cnDesignCSPage('RecommendationTitle')}
                          >
                            {companySiteCaseStudyMoreCases && (
                              <Typography.Text
                                size="xs"
                                weight="bold"
                                transform="uppercase"
                                as="span"
                              >
                                {companySiteCaseStudyMoreCases}
                              </Typography.Text>
                            )}
                          </div>
                        )}
                        <div className={cnDesignCSPage('RecommendationList')}>
                          {relatedCaseStudies.map(
                            item =>
                              item.title &&
                              item.slug && (
                                <Typography.Title
                                  size="tiny"
                                  key={item.id}
                                  as="link"
                                  to={generatePath(PATH.CASE_PAGE, {
                                    slug: item.slug,
                                  })}
                                >
                                  {item.title}
                                </Typography.Title>
                              )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </GridItem>
              </Grid>
            </SiteBlock>
          </div>

          <ContentBuilder
            aliases={PATH}
            items={mainContent}
            recaptchaKey={recaptchaKey}
            onSubmitForm={async body =>
              await mutationMainForm({
                body: { ...body, pageId: data?.id },
                [`${recaptchaFormKey}`]: body.token,
              })
            }
            dictionariesForm={dictionariesForm}
          />
          <div className="container">
            <div className={cnDesignCSPage('Bottom')}>
              <Grid
                breakpoints={{ desktop: { cols: 2 }, tablet: { cols: 2 } }}
                yAlign="center"
              >
                <GridItem className={cnDesignCSPage('BottomLeft')}>
                  {tags?.length > 0 && (
                    <TagGroup
                      items={tags.map(tag => {
                        return {
                          ...tag,
                          onClick: () =>
                            history.push(
                              generatePath(
                                `${PATH.SEARCH_PAGE}?tags=${tag.id}`,
                                {
                                  lang,
                                }
                              )
                            ),
                        };
                      })}
                    />
                  )}
                </GridItem>
                <GridItem className={cnDesignCSPage('BottomRight')}>
                  <SocialsShare
                    title={companySiteCaseStudyShare}
                    url={window.location.href}
                    onClick={onSocialShareHandler}
                  />
                </GridItem>
              </Grid>
            </div>
          </div>
          {formContent && (
            <ContentBuilder
              aliases={PATH}
              items={formContent}
              recaptchaKey={recaptchaKey}
              onSubmitForm={async body =>
                await mutationMainForm({
                  body: { ...body, pageId: data?.id },
                  [`${recaptchaFormKey}`]: body.token,
                })
              }
              dictionariesForm={dictionariesForm}
            />
          )}
        </div>
      </div>
    </div>
  );
};
